/**
 * LOCATION CONVERTER MODULE
 * Converts GPS coordinates to human-readable location names
 * Place this file in: src/avatarSystem/chatSystem/testing/locationConverter.js
 */

const TRAFFIC_CONFIG = {
  TOMTOM_KEY: process.env.REACT_APP_TOMTOM_KEY || "nF4LIbBHx737BrJdFHLUl0Ns8MxtoYHW",
};

/**
 * Fallback: Estimate location from coordinates (when API fails)
 * Uses approximate mapping for common areas
 */
const estimateLocationFromCoords = (coords) => {
  try {
    const [lat, lng] = coords.split(',').map(s => parseFloat(s.trim()));
    
    // New Jersey / New York area approximations
    if (lat >= 40.8 && lat <= 40.95 && lng >= -74.25 && lng <= -74.10) {
      // Northern NJ area
      if (lng < -74.20) return 'Clifton area';
      if (lng < -74.15) return 'Passaic area';
      return 'Paterson area';
    }
    
    if (lat >= 40.7 && lat <= 40.85 && lng >= -74.05 && lng <= -73.90) {
      return 'Newark area';
    }
    
    if (lat >= 40.70 && lat <= 40.80 && lng >= -74.10 && lng <= -73.95) {
      return 'Jersey City area';
    }
    
    // General fallback based on coordinates
    if (lat >= 40.5 && lat <= 41.0 && lng >= -74.5 && lng <= -73.5) {
      return 'northern New Jersey';
    }
    
    if (lat >= 40.5 && lat <= 41.0 && lng >= -74.5 && lng <= -73.0) {
      return 'the area';
    }
    
    return 'your route';
  } catch (error) {
    return 'your route';
  }
};

/**
 * Main function: Convert coordinates to natural location name
 * Example: "40.8668,-74.19368" → "Clifton, New Jersey"
 */
export const coordinatesToLocation = async (coords) => {
  console.log('🗺️ Converting coordinates to location:', coords);
  
  if (!coords || typeof coords !== 'string') {
    console.warn('⚠️ Invalid coordinates input:', coords);
    return 'your location';
  }
  
  // Try API first
  const address = await reverseGeocode(coords);
  
  // If API worked, extract location name
  if (address !== coords) {
    const location = extractLocationName(address);
    console.log('✅ Converted to:', location);
    return location;
  }
  
  // API failed, use coordinate-based estimation
  console.log('⚠️ API failed, using coordinate estimation');
  const estimated = estimateLocationFromCoords(coords);
  console.log('✅ Estimated location:', estimated);
  return estimated;
};

/**
 * Reverse geocode: Get full address from coordinates
 */
export const reverseGeocode = async (coords) => {
  try {
    console.log('🔄 Calling TomTom API for:', coords);
    
    // Check if API key looks valid
    if (!TRAFFIC_CONFIG.TOMTOM_KEY || 
        TRAFFIC_CONFIG.TOMTOM_KEY === 'ss' || 
        TRAFFIC_CONFIG.TOMTOM_KEY.length < 20 ||
        TRAFFIC_CONFIG.TOMTOM_KEY.startsWith('wsregwergergergergergerg')) {
      console.error('❌ Invalid or placeholder TomTom API key detected');
      console.error('   Current key:', TRAFFIC_CONFIG.TOMTOM_KEY.substring(0, 10) + '...');
      console.error('   Please set a valid key in .env as REACT_APP_TOMTOM_KEY');
      return coords;
    }
    
    const [lat, lng] = coords.split(',').map(s => s.trim());
    
    if (!lat || !lng || isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) {
      console.error('❌ Invalid coordinate format:', coords);
      return coords;
    }
    
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    
    if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
      console.error('❌ Coordinates out of valid range:', coords);
      return coords;
    }
    
    const url = `https://api.tomtom.com/search/2/reverseGeocode/${lat},${lng}.json?key=${TRAFFIC_CONFIG.TOMTOM_KEY}`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorDetails;
      try {
        const errorText = await response.text();
        try {
          errorDetails = JSON.parse(errorText);
        } catch (e) {
          errorDetails = errorText.substring(0, 200);
        }
      } catch (e) {
        errorDetails = 'Could not read error';
      }
      
      console.error('❌ TomTom API returned error:', {
        status: response.status,
        statusText: response.statusText,
        url: url.substring(0, 100) + '...',
        details: errorDetails
      });
      
      // If it's an auth error, log it clearly
      if (response.status === 401 || response.status === 403) {
        console.error('🔑 API Key Error - Check your REACT_APP_TOMTOM_KEY');
      }
      
      return coords;
    }
    
    const data = await response.json();
    
    if (!data.addresses || data.addresses.length === 0) {
      console.warn('⚠️ No addresses found for coordinates:', coords);
      return coords;
    }
    
    const address = data.addresses[0].address;
    
    const parts = [];
    
    if (address.streetNumber && address.streetName) {
      parts.push(`${address.streetNumber} ${address.streetName}`);
    } else if (address.streetName) {
      parts.push(address.streetName);
    }
    
    if (address.municipality) {
      parts.push(address.municipality);
    } else if (address.municipalitySubdivision) {
      parts.push(address.municipalitySubdivision);
    }
    
    if (address.countrySubdivision) {
      parts.push(address.countrySubdivision);
    }
    
    if (address.country && address.country !== 'United States') {
      parts.push(address.country);
    }
    
    const formatted = parts.filter(p => p && p.length > 0).join(', ');
    
    if (formatted && formatted.length > 0) {
      console.log('✅ Formatted address:', formatted);
      return formatted;
    } else {
      console.warn('⚠️ Could not format address, using coords');
      return coords;
    }
    
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('⏱️ API request timeout after 5 seconds');
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('❌ Network error - check internet connection');
    } else {
      console.error('❌ Reverse geocoding failed:', error.message || error);
    }
    
    return coords;
  }
};

/**
 * Extract natural location name from full address
 * "123 Main St, Clifton, New Jersey" → "Clifton, New Jersey"
 */
const extractLocationName = (address) => {
  // Check if it's coordinates (format: "number,number")
  if (address.includes(',')) {
    const firstPart = address.split(',')[0].trim();
    if (!isNaN(parseFloat(firstPart)) && firstPart.length < 20) {
      return 'your location';
    }
  }
  
  const parts = address.split(',').map(s => s.trim());
  
  if (parts.length === 0) {
    return 'your location';
  }
  
  // If only one part, return it
  if (parts.length === 1) {
    return parts[0];
  }
  
  // ✅ FIX: For "Street Address, City, State" format
  // Return "City, State" (skip the first part which is the street)
  if (parts.length >= 3) {
    const cityPart = parts[1]; // "Little Falls"
    const statePart = parts[2]; // "NJ"
    return `${cityPart}, ${statePart}`;
  }
  
  // If only 2 parts, check if first is a street address
  if (parts.length === 2) {
    const firstPart = parts[0];
    const secondPart = parts[1];
    
    // If first part starts with a number, it's likely a street address
    // Return the second part (city/state)
    if (/^\d/.test(firstPart)) {
      return secondPart;
    }
    
    // Otherwise return both parts
    return `${firstPart}, ${secondPart}`;
  }
  
  return 'your location';
};

/**
 * Format location for natural speech in podcast
 */
export const formatLocationForSpeech = (location) => {
  if (location.includes(',') && !isNaN(parseFloat(location.split(',')[0]))) {
    return 'your area';
  }
  
  const phrases = [
    `in ${location}`,
    `around ${location}`,
    `for ${location}`,
    `the ${location} area`
  ];
  
  const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];
  
  return randomPhrase;
};

/**
 * Get just the city name (for shorter references)
 * "Clifton, New Jersey" → "Clifton"
 */
export const getCityName = (address) => {
  // ✅ Check if it's coordinates
  if (address.includes(',') && !isNaN(parseFloat(address.split(',')[0]))) {
    return null; // Return null instead of the address
  }
  
  // ✅ Check if it's already been filtered to "your location"
  if (address === 'your location' || address === 'your area') {
    return null;
  }
  
  const parts = address.split(',').map(s => s.trim());
  
  // ✅ For "Street Name, City, State" format, return the city (second part)
  if (parts.length >= 2) {
    const cityPart = parts[1];
    
    // Make sure it's not a street address (doesn't start with number)
    if (cityPart && !/^\d/.test(cityPart) && cityPart.length > 2) {
      return cityPart;
    }
  }
  
  // ✅ Find the first part that doesn't start with a number
  const cityPart = parts.find(part => !/^\d/.test(part) && part.length > 2);
  
  return cityPart || null;
};

/**
 * Default export with all functions
 */
export default {
  coordinatesToLocation,
  reverseGeocode,
  extractLocationName,
  formatLocationForSpeech,
  getCityName
};